import { useRef, useState } from "react";
import { C } from "../utils/tokens";
import { Field, FInput, FTA } from "../components/primitives";
import {
  onlyText,
  onlyPhone,
  validateNome,
  validateEmail,
  validatePhone,
  validateLinkedin,
} from "../utils/validation";

const ErrMsg = ({ msg }) =>
  msg ? (
    <div style={{ fontSize: 11, color: C.danger, marginTop: 3 }}>{msg}</div>
  ) : null;

const Step1 = ({ d, s }) => {
  const fileRef = useRef();
  const [errors, setErrors] = useState({});

  const setField = (key, val) => {
    s({ ...d, [key]: val });
    if (errors[key]) setErrors((e) => ({ ...e, [key]: "" }));
  };

  const handleBlur = (key, validator, val) => {
    setErrors((e) => ({ ...e, [key]: validator(val) }));
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Immagine troppo grande. Massimo 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => s({ ...d, foto: ev.target.result });
    reader.readAsDataURL(file);
  };

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 18 }}
      className="au"
    >
      <div>
        <h2
          style={{
            fontFamily: "'Syne',sans-serif",
            fontSize: 26,
            fontWeight: 800,
            marginBottom: 5,
          }}
        >
          Chi sei?
        </h2>
        <p style={{ color: C.muted, fontSize: 13.5 }}>
          Informazioni base del tuo profilo.
        </p>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
        <div
          onClick={() => !d.foto && fileRef.current.click()}
          style={{
            width: 88,
            height: 88,
            borderRadius: "50%",
            flexShrink: 0,
            border: `2px dashed ${d.foto ? C.goldBorder : C.border}`,
            background: d.foto ? "transparent" : C.surface,
            overflow: "hidden",
            cursor: d.foto ? "default" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "border-color 0.2s",
          }}
          onMouseEnter={(e) =>
            !d.foto && (e.currentTarget.style.borderColor = C.gold)
          }
          onMouseLeave={(e) =>
            !d.foto && (e.currentTarget.style.borderColor = C.border)
          }
        >
          {d.foto ? (
            <img
              src={d.foto}
              alt="Foto profilo"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <div style={{ textAlign: "center", pointerEvents: "none" }}>
              <div style={{ fontSize: 24, marginBottom: 4 }}>👤</div>
              <div
                style={{
                  fontSize: 9,
                  color: C.muted,
                  fontFamily: "'DM Mono',monospace",
                  letterSpacing: "0.05em",
                }}
              >
                FOTO
              </div>
            </div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: "'Syne',sans-serif",
              fontWeight: 600,
              fontSize: 13,
              color: C.text,
              marginBottom: 4,
            }}
          >
            Foto profilo{" "}
            <span style={{ color: C.muted, fontWeight: 400 }}>(opzionale)</span>
          </div>
          <div
            style={{
              fontSize: 12,
              color: C.muted,
              lineHeight: 1.6,
              marginBottom: 10,
            }}
          >
            JPG o PNG, max 5MB.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={() => fileRef.current.click()}
              style={{
                padding: "6px 14px",
                borderRadius: 7,
                fontSize: 12,
                border: `1px solid ${C.goldBorder}`,
                background: C.goldDim,
                color: C.gold,
                fontFamily: "'Syne',sans-serif",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {d.foto ? "↺ Cambia foto" : "📁 Carica foto"}
            </button>
            {d.foto && (
              <button
                onClick={() => s({ ...d, foto: null })}
                style={{
                  padding: "6px 14px",
                  borderRadius: 7,
                  fontSize: 12,
                  border: "1px solid rgba(255,82,82,0.25)",
                  background: "rgba(255,82,82,0.08)",
                  color: C.danger,
                  fontFamily: "'Syne',sans-serif",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                ✕ Rimuovi
              </button>
            )}
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          onChange={handleImage}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Field label="Nome">
          <FInput
            value={d.nome}
            onChange={(v) => setField("nome", onlyText(v))}
            onBlur={() => handleBlur("nome", validateNome, d.nome)}
            placeholder="Mario"
          />
          <ErrMsg msg={errors.nome} />
        </Field>
        <Field label="Cognome">
          <FInput
            value={d.cognome}
            onChange={(v) => setField("cognome", onlyText(v))}
            onBlur={() => handleBlur("cognome", validateNome, d.cognome)}
            placeholder="Rossi"
          />
          <ErrMsg msg={errors.cognome} />
        </Field>
      </div>

      <Field label="Titolo professionale">
        <FInput
          value={d.ruolo}
          onChange={(v) => setField("ruolo", v)}
          placeholder="Frontend Developer · UX Designer · Insegnante..."
        />
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Field label="Email">
          <FInput
            type="email"
            value={d.email}
            onChange={(v) => setField("email", v)}
            onBlur={() => handleBlur("email", validateEmail, d.email)}
            placeholder="mario@email.com"
          />
          <ErrMsg msg={errors.email} />
        </Field>
        <Field label="Telefono">
          <FInput
            value={d.telefono}
            onChange={(v) => setField("telefono", onlyPhone(v))}
            onBlur={() => handleBlur("telefono", validatePhone, d.telefono)}
            placeholder="+39 333 000 0000"
          />
          <ErrMsg msg={errors.telefono} />
        </Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Field label="Città">
          <FInput
            value={d.citta}
            onChange={(v) => setField("citta", onlyText(v))}
            placeholder="Milano"
          />
        </Field>
        <Field label="LinkedIn / Portfolio">
          <FInput
            value={d.linkedin}
            onChange={(v) => setField("linkedin", v)}
            onBlur={() => handleBlur("linkedin", validateLinkedin, d.linkedin)}
            placeholder="linkedin.com/in/..."
          />
          <ErrMsg msg={errors.linkedin} />
        </Field>
      </div>

      <Field label="Sommario">
        <FTA
          value={d.sommario}
          onChange={(v) => setField("sommario", v)}
          placeholder="Breve presentazione professionale..."
          rows={4}
        />
      </Field>
    </div>
  );
};

export default Step1;
